// src/components/MsoDefinitionView.tsx
//
// ★★ ANNEX A 22 — a rule definition READ, never edited: applicability, precedence, the decision
// table and the rounding mode, in the officer's words; and the side-by-side comparison of two
// versions. Shared by the rule list (version comparison) and the enquiry detail (the exact
// definition a calculation used).
//
// ⚠ A change is announced in TEXT ("Added", "Removed", "Changed: shares") and drawn as a coloured badge
// from the shared category mapping (StatusBadge.tsx), so it is readable by anyone who cannot tell colours apart.
import { conditionText, diffDecisionTables, diffDefinitionSettings, sharesText, type RowDiff } from '../lib/mso-diff'
import { CategoryBadge } from './StatusBadge'
import { MSO_ROUNDING_LABELS } from '../lib/mso-vocabulary'
import type { MsoDecisionRow, MsoRuleDefinition } from '../lib/types'

export function ConditionChips({ conditions }: { conditions: MsoDecisionRow['when'] }) {
  if (conditions.length === 0) return <span className="text-sm text-[var(--sgds-body-color-subtle)]">Always</span>
  return (
    <ul className="flex flex-wrap gap-1 list-none p-0 m-0">
      {conditions.map((condition, index) => (
        <li
          key={`${condition.field}-${index}`}
          className="text-xs px-2 py-0.5 border border-[var(--sgds-border-color-default)] rounded"
        >
          {conditionText(condition)}
        </li>
      ))}
    </ul>
  )
}

export function ApplicabilityView({ definition }: { definition: MsoRuleDefinition }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--sgds-color-subtle)] mb-1">
        Claims a bill when any one variant holds
      </p>
      <ol className="flex flex-col gap-2 list-none p-0 m-0">
        {definition.applicability.map((variant, index) => (
          <li key={index} className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold">Variant {index + 1}</span>
            <ConditionChips conditions={variant} />
          </li>
        ))}
      </ol>
    </div>
  )
}

export function DecisionTableView({ table }: { table: readonly MsoDecisionRow[] }) {
  return (
    <div className="pt-table-wrap">
      <table className="pt-table">
        <caption className="sr-only">Decision table, one row per scenario</caption>
        <thead>
          <tr>
            <th scope="col" data-unsortable="a decision table has no order, only a unique match; it is read as written, not sorted">
              Row
            </th>
            <th scope="col" data-unsortable="a decision table has no order, only a unique match; it is read as written, not sorted">
              Applies when
            </th>
            <th scope="col" data-unsortable="a decision table has no order, only a unique match; it is read as written, not sorted">
              Officer share
            </th>
          </tr>
        </thead>
        <tbody>
          {table.map((row) => (
            <tr key={row.id}>
              <td>
                <span className="pt-mono font-semibold">{row.id}</span>
                <br />
                <span className="text-sm">{row.label}</span>
              </td>
              <td>
                <ConditionChips conditions={row.when} />
              </td>
              <td className="text-sm">{sharesText(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** One definition, read in full. */
export function DefinitionView({ definition }: { definition: MsoRuleDefinition }) {
  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-lg">
        <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Precedence</dt>
        <dd className="pt-tabular">{definition.precedence}</dd>
        <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Rounding</dt>
        <dd>
          {MSO_ROUNDING_LABELS[definition.rounding.mode] ?? definition.rounding.mode}, per component, remainder to Government
        </dd>
      </dl>
      <ApplicabilityView definition={definition} />
      <DecisionTableView table={definition.table} />
    </div>
  )
}

const CHANGE_WORDS: Readonly<Record<RowDiff['change'], string>> = {
  ADDED: 'Added',
  REMOVED: 'Removed',
  CHANGED: 'Changed',
  UNCHANGED: 'Unchanged',
}

function DiffSide({ row }: { row: MsoDecisionRow | null }) {
  if (row === null) return <p className="text-sm text-[var(--sgds-body-color-subtle)] m-0">Not in this version.</p>
  return (
    <div>
      <p className="text-sm m-0">{row.label}</p>
      <div className="my-1">
        <ConditionChips conditions={row.when} />
      </div>
      <p className="text-sm font-semibold m-0">{sharesText(row)}</p>
    </div>
  )
}

export interface VersionDiffProps {
  readonly leftLabel: string
  readonly rightLabel: string
  readonly left: MsoRuleDefinition
  readonly right: MsoRuleDefinition
}

/** Two versions side by side: every row id in either table, with what changed in words. */
export function VersionDiff({ leftLabel, rightLabel, left, right }: VersionDiffProps) {
  const rows = diffDecisionTables(left.table, right.table)
  const settings = diffDefinitionSettings(left, right)
  const changed = rows.filter((r) => r.change !== 'UNCHANGED').length
  return (
    <section aria-label={`Comparison of ${leftLabel} and ${rightLabel}`} className="pt-card">
      <h2 className="text-base font-semibold mb-1">
        {leftLabel} compared with {rightLabel}
      </h2>
      <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-3" role="status">
        {changed === 0 && settings.length === 0
          ? 'The two decision tables and settings are identical.'
          : `${changed} of ${rows.length} decision row(s) differ.`}
      </p>
      {settings.length > 0 && (
        <ul className="mb-3 text-sm list-disc pl-5">
          {settings.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      )}
      <ul className="flex flex-col gap-3 list-none p-0 m-0">
        {rows.map((row) => (
          <li key={row.id} className="pt-well p-3">
            <p className="m-0 mb-2">
              <span className="pt-mono font-semibold">{row.id}</span>{' '}
              <CategoryBadge kind="diff" value={row.change} label={CHANGE_WORDS[row.change]} />
              {row.what.length > 0 && <span className="text-sm">{`: ${row.what.join(', ')}`}</span>}
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold text-[var(--sgds-color-subtle)] m-0 mb-1">{leftLabel}</p>
                <DiffSide row={row.before} />
              </div>
              <div>
                <p className="text-xs font-semibold text-[var(--sgds-color-subtle)] m-0 mb-1">{rightLabel}</p>
                <DiffSide row={row.after} />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
